import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ICurrencyPrice, IProduct, IProductCurrency } from 'src/app/features/produits/models/products.models';
import { CatalogsService } from '../../services/catalogs.services';
import { distinctUntilChanged, filter, Subject, takeUntil, take, combineLatest, Observable } from 'rxjs';
import { GetAgents } from 'src/app/features/agents/store/agents.actions';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { IAgent } from 'src/app/features/agents/models/agents.models';
import { ICatalog, IGeneralRules, IHandlingUnitType, INewCatalogDTO } from '../../models/catalogs.models';
import { MatDialog } from '@angular/material/dialog';
import { AddAgentCatalog, AddAgentCatalogSuccess, AddCatalog, AddCatalogSuccess, DownloadCatalog, GetCatalogById, GetCatalogByIdSuccess, GetCatalogProducts, GetCatalogProductsSuccess, LoadMoreCatalogProductsSuccess, SetCatalogLoader, UpdateCatalog, UpdateCatalogSuccess, UpdateAgentCatalog, UpdateAgentCatalogSuccess, UpdateAgentCatalogError, GetCatalogAudience, GetCatalogAudienceSuccess, LoadMoreCatalogAudience, LoadMoreCatalogAudienceSuccess, ClearCatalogAudience } from '../../store/catalogs.actions';
import { Actions, ofType } from '@ngrx/effects';
import { NewCatalogConfirmationDialogComponent } from './new-catalog-confirmation-dialog/new-catalog-confirmation-dialog.component';
import { ICurrency, ICustomer, ILocation } from 'src/app/features/customers/models/customers.models';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { ShareCatalogComponent } from '../share-catalog/share-catalog.component';
import { ConfirmNotificationComponent } from '../confirm-notification/confirm-notification.component';
import { AgentPortfolioAddDialogComponent } from 'src/app/features/agents/components/agent-details/agent-portfolio/agent-portfolio-add-dialog/agent-portfolio-add-dialog.component';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { FormControl } from '@angular/forms';
import { areSelectedProductsValid, getValidationTooltipMessage } from 'src/app/shared/validators/products-prices-validators';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ISubsidiary } from 'src/app/features/subsidiaries/models/subsidiary.model';
import { IObjectCustomFields } from 'src/app/features/settings/models/settings.models';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

export interface INewCatalog {
  subsidiary_id?: number | null;
  name: string,
  catalogtId?: number,
  description: string,
  status: string,
  cover: File,
  coverUrl: string | null,
  expiration_date: string,
  pricing_rule: 'margin' | 'discount' | null,
  overall_pricing_value: number;
  has_extra_discount: boolean;
  extra_discount: number | null,
  extra_discount_blocking: boolean,
  min_order_amount: number | null,
  products: IProduct[];
  agents: IAgent[];
  customers: ICustomer[];
  locations: ILocation[];
  companies: ICustomer[];
  quantity_rules: IGeneralRules;
  locations_ids?: number[];
  product_ids?: number[];
  loaded_product_ids?: number[];
  is_multi_currency?: boolean;
  default_currency?: number;
  enabled_currencies?: number[];
  show_full_pricing_data?: boolean;
  enabled_handling_unit_ids?: number[];
  default_handling_unit_id?: number | null;
  custom_fields?: IObjectCustomFields['values'];
}
@Component({
  selector: 'app-new-catalog',
  templateUrl: './new-catalog.component.html',
  styleUrls: ['./new-catalog.component.scss']
})
export class NewCatalogComponent implements OnInit {
  agentId!: number;
  catalogtId!: number;
  source: 'agents' | 'catalogs' = 'catalogs';

  rightPanelActiveTab: 'products' | 'agents' | 'audience' | 'price-rules' = 'products';

  newCatalog!: INewCatalog;
  catalog!: ICatalog;
  totalSKUs: number = 0;
  totalStock: number = 0;
  assignedAgents: number = 1; // Source agent should be assigned by default
  assignedAudiences: number = 0;
  validToSubmit: boolean = true;
  saveAs: 'Draft' | 'Approved' = 'Draft';
  selectedCompanies: number[] = [];
  assignedLocations: number[] = [];
  assignedCustomers: ICustomer[] = [];
  showDeleteButton: boolean = false;
  clearSelectedCompanies: string = '';
  isEdit: boolean = false;
  mode: 'edit-products' | 'add-products' | null = null;
  availableCurrencies: ICurrency[] = [];
  agentCatalogtId!: number;

  // Pagination for audience list
  companiesMetadata: any = null;
  isLoadingMoreCompanies: boolean = false;

  destroy$ = new Subject<void>()
  currencies$: Observable<ICurrency[]> = this.store.select((state) => state.customers.allCurrencies);
  currencyControl = new FormControl();

  audienceCompanies$ = this.store.select((state) => state.catalogs.audienceCompanies);
  audienceMetadata$ = this.store.select((state) => state.catalogs.audienceMetadata);
  productsMetadata$ = this.store.select((state) => state.catalogs.productsMetadata);

  private previousCatalogEnabledHUIds: number[] = [];
  private previousCatalogDefaultHUId: number | null = null;

  constructor(private router: Router, private route: ActivatedRoute, private catalogService: CatalogsService, private store: Store<AppState>, public dialog: MatDialog, private actions$: Actions, private translateService: TranslateService, public featureFlags: FeatureFlagService) {}

  ngOnInit(): void {
    this.mode = this.route.snapshot.queryParamMap.get('mode') as 'edit-products' | 'add-products';
    const data = this.catalogService.getNewCatalogData();
    if(!data || !this.mode) {
      this.initializeNewCatalogDefaults();
      this.catalogService.setNewCatalogData(this.newCatalog);
    }
    this.currencyControl.setValue(this.newCatalog?.default_currency || null);
    this.source = this.router.url.includes('agents') ? 'agents' : 'catalogs';
    this.catalogtId = Number(this.route.snapshot.paramMap.get('id')!);
    if(this.source === 'agents') {
      this.agentId = Number(this.route.snapshot.queryParamMap.get('agentId')!);
    }

    if(this.catalogtId) {
      this.isEdit = true;
      if(this.mode !== 'add-products') {
        this.fetchActions();
      }
    }

    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParams: { mode: null },
      queryParamsHandling: 'merge'
    });

    const byId$ = this.actions$.pipe(ofType(GetCatalogByIdSuccess));
    const products$ = this.actions$.pipe(ofType(GetCatalogProductsSuccess));

    combineLatest([byId$, products$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([byIdAction, productsAction]) => {
        const catalog = byIdAction.catalog as ICatalog;
        const products = (productsAction.products ?? []) as IProduct[];

        this.catalog = catalog;

        this.newCatalog = {
          subsidiary_id: catalog.subsidiary_id || undefined,
          name: catalog.name,
          catalogtId: catalog.id,
          status: catalog.status,
          description: catalog.description!,
          cover: new File([], ''),
          coverUrl: catalog.image ? environment.BASE_URL_API + catalog.image : null,
          expiration_date: catalog.end_date!,
          pricing_rule: catalog.overall_pricing_rule === 'margin_on_cost' ? 'margin' : 'discount',
          overall_pricing_value: catalog.overall_pricing_value || 0,
          min_order_amount: catalog.min_order_amount || null,
          has_extra_discount: catalog.max_extra_discount ? true : false,
          extra_discount: catalog.max_extra_discount || null,
          extra_discount_blocking: catalog.block_order_on_max_extra_discount ? true : false,
          agents: catalog.agents || [],
          customers: [],  // Will be loaded via pagination
          products: products.map((product: IProduct) => {
            const productDiscount = product.discount ?? 0;
            const overallPricingValue = catalog.overall_pricing_value || 0;
            const hasCustomDiscount = productDiscount !== 0 && productDiscount !== overallPricingValue;
            
            return {
              ...product,
              currencies: product.currencies?.map((currency: IProductCurrency) => ({ ...currency })),
              default_handling_unit_id: this.getProductDefaultHUId(product),
              hasCustomDiscount: hasCustomDiscount,
            } as any;
          }),
          locations: catalog.locations!,
          companies: [],  // Will be loaded via pagination
          locations_ids: [],  // Will be populated after companies load
          quantity_rules: {
            min_qte: catalog.min_qte,
            max_qte: catalog.max_qte,
            increment: catalog.increment,
            can_be_sold_out_of_stock: catalog.can_be_sold_out_of_stock,
            show_stock: catalog.show_stock,
          },
          is_multi_currency: catalog.is_single_currency === false,
          default_currency: catalog.default_currency?.id,
          enabled_currencies: catalog.currencies?.map((currency: ICurrency) => currency.id),
          show_full_pricing_data: (catalog as any).show_full_pricing_data ?? true,
          enabled_handling_unit_ids: catalog.enabled_handling_units?.map(hu => hu.id) || [],
          default_handling_unit_id: catalog.default_handling_unit?.id || null,
          // Product preservation: store all product IDs from catalog and track which were loaded
          product_ids: catalog.product_ids || [],
          loaded_product_ids: products.map((p: IProduct) => p.id).filter((id): id is number => id !== undefined),
        };

        // Use nbr_products from catalog, but if it's 0 and we have products, use product count
        if (catalog.nbr_products != null && catalog.nbr_products > 0) {
          this.totalSKUs = catalog.nbr_products;
        } else if (products && products.length > 0) {
          // If nbr_products is 0 but we have products, use product count
          this.totalSKUs = products.length;
        } else {
          this.totalSKUs = catalog.nbr_products || 0;
        }
        this.assignedAgents = this.newCatalog.agents.length;

        // Store all assigned location IDs from catalog (preserves full list for pagination)
        const rawIds = catalog.locations_ids || catalog.locations?.map(l => l.id!) || [];
        this.assignedLocations = [...new Set(rawIds.map(id => Number(id)))];
        this.newCatalog.locations_ids = this.assignedLocations;
        this.assignedCustomers = [];  // Will be loaded via pagination
        this.assignedAudiences = this.assignedLocations.length;
        this.totalStock = catalog.total_stock ?? 0;
        this.availableCurrencies = catalog.currencies || [];
        this.currencyControl.setValue(this.newCatalog.default_currency || null);
        this.catalogService.setNewCatalogData(this.newCatalog);
        
        this.previousCatalogEnabledHUIds = [...(this.newCatalog.enabled_handling_unit_ids || [])];
        this.previousCatalogDefaultHUId = this.newCatalog.default_handling_unit_id || null;

        // Load first page of companies with pagination
        this.loadInitialCompaniesPage();
      });

    // Subscribe to audience companies success action (initial load)
    this.actions$.pipe(
      ofType(GetCatalogAudienceSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.handleAudienceCompaniesLoaded(action.companies, action.metadata, false);
    });

    // Subscribe to load more audience companies success action
    this.actions$.pipe(
      ofType(LoadMoreCatalogAudienceSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.handleAudienceCompaniesLoaded(action.companies, action.metadata, true);
    });

    this.catalogService.newCatalogData$.pipe(
      filter((data) => data != null),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.newCatalog = data as INewCatalog;

      if(this.newCatalog?.products?.length) {
        // Set totalSKUs from products
        // For new catalogs: calculate unique SKUs, fallback to product count
        // For edit mode: if totalSKUs is 0 or not set, use product count
        if (!this.isEdit) {
          const calculatedSKUs = this.calculateUniqueSKUs(this.newCatalog.products);
          // If calculated SKUs is 0 but products exist, use product count
          this.totalSKUs = calculatedSKUs > 0 ? calculatedSKUs : this.newCatalog.products.length;
        } else if (this.totalSKUs === 0 || this.totalSKUs == null) {
          // In edit mode: if totalSKUs is 0 or not set but we have products, use product count
          this.totalSKUs = this.newCatalog.products.length;
        }
        // Total stock: use backend value when in edit mode (catalog has total_stock); otherwise sum from loaded products
        if (this.isEdit && this.catalog?.total_stock != null) {
          this.totalStock = this.catalog.total_stock;
        } else {
          this.totalStock = this.newCatalog.products.reduce((acc, product) => acc + (product.stock?.quantity || 0), 0);
        }
        this.currencies$.pipe(take(1)).subscribe((list) => {
          this.availableCurrencies = list.filter((currency) => data?.enabled_currencies?.includes(currency.id)) || [];
          // add default currency to availableCurrencies if not already in the list
          if(!this.availableCurrencies.some((currency) => currency.id === data?.default_currency)) {
            if(data?.default_currency) {
              this.availableCurrencies.push({
                id: data.default_currency,
                name: list.find((currency) => currency.id === data?.default_currency)?.name || '',
                full_name: list.find((currency) => currency.id === data?.default_currency)?.full_name || '',
                symbol: list.find((currency) => currency.id === data?.default_currency)?.symbol || '',
                is_default: list.find((currency) => currency.id === data?.default_currency)?.is_default || false,
                is_active: list.find((currency) => currency.id === data?.default_currency)?.is_active || false,
              });
            }
          }
        });
      }

      if(this.newCatalog.agents?.length >= 0) {
        this.assignedAgents = this.newCatalog.agents.length;
      }

      this.assignedLocations = this.newCatalog.locations_ids || [];
      this.assignedCustomers = this.newCatalog.customers || [];
    })

    this.actions$.pipe(
      ofType(AddAgentCatalogSuccess, AddCatalogSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.catalogService.setNewCatalogData(null);
      if(action.catalog?.id != null) {
        this.catalogtId = action.catalog.id;
        this.store.dispatch(GetCatalogProducts({ id: this.catalogtId, page: 1, limit: 100 }));
      }
      if(action.type === AddCatalogSuccess.type && action.catalog && action.catalog.status === 'Approved') {
        this.openShareCatalog('add')
      } else {
        const dialogRef = this.dialog.open(NewCatalogConfirmationDialogComponent, {
          data: {
            status: this.saveAs,
            hasSourceAgent: this.newCatalog?.agents?.filter((agent: IAgent) => agent.id === Number(this.agentId)).length,
            source: this.source,
            agentId: isNaN(Number(this.agentId)) ? null : this.agentId
          }
        })
        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (!result) {
            if (this.source === 'agents') {
              this.router.navigate(['catalogs/agents-catalog/edit-catalog', this.catalogtId]);
            }
          }
          if(this.source === 'agents' && this.agentId && !isNaN(this.agentId)) {
            if(result) {
              this.router.navigate(this.saveAs === 'Draft' ? ['/catalogs'] : ['/agents', this.agentId]);
              this.catalogService.setNewCatalogData(null);
            }
          } else {
            if(result) {
              this.router.navigate(['catalogs']);
            }
          }
        });
      }
      if (this.newCatalog) {
        this.newCatalog.status = action.catalog?.status;
      }
      this.isEdit = true;
    });

    this.actions$.pipe(
      ofType(UpdateAgentCatalogSuccess, UpdateCatalogSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      // When it just saving an agent catalog as draft, we don't need to show the confirmation dialog
      if(this.source === 'agents' && this.saveAs === 'Draft') return;

      if(action.catalog?.id != null) {
        this.catalogtId = action.catalog.id;
      }
      
      // Update totalSKUs from response using nbr_products field (like original)
      // If in edit mode and nbr_products is 0, use selected products count
      if (action.catalog?.nbr_products != null) {
        if (action.catalog.nbr_products > 0) {
          this.totalSKUs = action.catalog.nbr_products;
        } else if (this.isEdit && this.newCatalog?.products && this.newCatalog.products.length > 0) {
          // In edit mode: if backend returns 0 but we have selected products, use product count
          this.totalSKUs = this.newCatalog.products.length;
        } else if (this.newCatalog?.products && this.newCatalog.products.length > 0) {
          // If backend returns 0 but we have products, calculate from products
          this.totalSKUs = this.calculateUniqueSKUs(this.newCatalog.products);
        } else {
          this.totalSKUs = action.catalog.nbr_products;
        }
      }
      
      if(action.type === UpdateCatalogSuccess.type && action.catalog && action.catalog.status === 'Approved') {
        this.openShareCatalog('edit')
      } else {
        const dialogRef = this.dialog.open(NewCatalogConfirmationDialogComponent, {
          data: {
            status: 'Approved',
            hasSourceAgent: this.newCatalog.agents?.filter((agent: IAgent) => agent.id === Number(this.agentId)).length,
            source: this.source,
            agentId: isNaN(Number(this.agentId)) ? null : this.agentId
          }
        })
        dialogRef.afterClosed().subscribe((result: boolean) => {
          if(result) {
            if(this.source === 'agents' && this.agentId && !isNaN(this.agentId)) {
              this.router.navigate(['/agents', this.agentId]);
              this.catalogService.setNewCatalogData(null);
            } else {
              this.router.navigate(['catalogs']);
            }
          }
        });
      }
      this.newCatalog.status = action.catalog?.status;
    });

    this.actions$.pipe(
      ofType(UpdateCatalogSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      // Use nbr_products from the response (like original) - this is the field
      // If in edit mode and nbr_products is 0, use selected products count
      if (action.catalog?.nbr_products != null) {
        if (action.catalog.nbr_products > 0) {
          this.totalSKUs = action.catalog.nbr_products;
        } else if (this.isEdit && this.newCatalog?.products && this.newCatalog.products.length > 0) {
          // In edit mode: if backend returns 0 but we have selected products, use product count
          this.totalSKUs = this.newCatalog.products.length;
        } else if (this.newCatalog?.products && this.newCatalog.products.length > 0) {
          // If backend returns 0 but we have products, calculate from products
          this.totalSKUs = this.calculateUniqueSKUs(this.newCatalog.products);
        } else {
          this.totalSKUs = action.catalog.nbr_products;
        }
      } else if (this.isEdit && this.newCatalog?.products && this.newCatalog.products.length > 0) {
        // In edit mode: if nbr_products not in response, use selected products count
        this.totalSKUs = this.newCatalog.products.length;
      } else if (this.newCatalog?.products && this.newCatalog.products.length > 0) {
        // Fallback to calculating from local products if nbr_products not in response
        this.totalSKUs = this.calculateUniqueSKUs(this.newCatalog.products);
      }
      this.fetchActions();
    });

    // Listen for more products loaded (pagination) and update the catalog service
    this.actions$.pipe(
      ofType(LoadMoreCatalogProductsSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      const currentData = this.catalogService.getNewCatalogData();
      if(currentData && action.products) {
        const processedNewProducts = action.products.map((product: IProduct) => ({
          ...product,
          default_handling_unit_id: this.getProductDefaultHUId(product),
        } as any));
        
        const updatedProducts = [...(currentData.products || []), ...processedNewProducts];
        const newLoadedIds = processedNewProducts.map((p: IProduct) => p.id).filter((id): id is number => id !== undefined);
        this.catalogService.setNewCatalogData({
          ...currentData,
          products: updatedProducts,
          loaded_product_ids: [...(currentData.loaded_product_ids || []), ...newLoadedIds]
        });
        // Keep total stock from catalog (backend); do not recalculate from paginated products
        if (this.catalog?.total_stock != null) {
          this.totalStock = this.catalog.total_stock;
        } else {
          this.totalStock = updatedProducts.reduce((acc, product) => acc + (product.stock?.quantity || 0), 0);
        }
        // Recalculate totalSKUs when new products are loaded
        // In edit mode: if totalSKUs is 0, use product count
        if (!this.isEdit) {
          this.totalSKUs = this.calculateUniqueSKUs(updatedProducts);
        } else if (this.totalSKUs === 0 && updatedProducts.length > 0) {
          // In edit mode: if totalSKUs is 0 but we have products, use product count
          this.totalSKUs = updatedProducts.length;
        }
      }
    });

    if(this.assignedLocations.length === 0) {
      this.assignedLocations = this.newCatalog.locations_ids || [];
    }
  }

  fetchActions() {
    this.store.dispatch(SetCurrentElementId({ currentElementId: this.catalogtId }))
    this.store.dispatch(GetCatalogById({ id: this.catalogtId }))
    this.store.dispatch(GetCatalogProducts({ id: this.catalogtId, page: 1, limit: 25 }))
  }

  isValidToSave(as: 'Draft' | 'Approved') {
    if(this.source === 'agents') {
      if(as === 'Draft') {
        return this.newCatalog && this.newCatalog.name && this.newCatalog.products && this.newCatalog.products.length > 0
      }

      if(as === 'Approved') {
        return this.newCatalog && this.newCatalog.name && this.newCatalog.products.length > 0 && this.assignedAgents > 0;
      }
    } else {
      if(as === 'Draft') {
        return this.newCatalog && this.newCatalog.name;
      }

      if(as === 'Approved') {
        return this.newCatalog && this.newCatalog.name && this.newCatalog.products.length > 0 && this.newCatalog.customers.length > 0 && this.areSelectedProductsValid();
      }
    }

    return false;
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.isEdit ? 'edit' : 'create' };
  }

  editCatalogProducts(): void {
    if(this.source === 'agents') {
      if(this.isEdit) {
        this.router.navigate(['/agents', 'edit-catalog', this.catalogtId, 'add-products'], { queryParams: { agent_id: this.agentId } });
      }
      else {
        this.router.navigate(['/agents', 'new-catalog', 'add-products'], { queryParams: { agent_id: this.agentId } });
      }
    } else {
      if(this.isEdit) {
        this.router.navigate(['/catalogs', 'edit-catalog', this.catalogtId, 'add-products']);
      } else {
        this.router.navigate(['/catalogs', 'new-catalog', 'add-products']);
      }
    }
  }

  goBack(): void {
    this.isEdit = false;
    this.catalogService.setNewCatalogData(null)

    if(this.source === 'agents') {
      if(this.agentId && !isNaN(this.agentId)){
        this.router.navigate(['/agents', this.agentId]);
      } else {
        this.router.navigate(['/catalogs']);
      }
    } else {
      this.router.navigate(['/catalogs']);
    }
    this.initializeNewCatalogDefaults();
  }

  private initializeNewCatalogDefaults(): void {
    this.newCatalog = {
      subsidiary_id: undefined,
      name: '',
      description: '',
      status: 'Draft',
      cover: new File([], ''),
      coverUrl: null,
      expiration_date: '',
      pricing_rule: 'discount',
      overall_pricing_value: 0,
      has_extra_discount: false,
      extra_discount: null,
      extra_discount_blocking: false,
      min_order_amount: null,
      products: [],
      agents: [],
      customers: [],
      locations: [],
      companies: [],
      quantity_rules: {
        min_qte: 1,
        max_qte: null,
        increment: 1,
        can_be_sold_out_of_stock: false,
        show_stock: true
      },
      is_multi_currency: false,
      default_currency: LocalStorageService.get('currentUser')?.user?.currency?.id,
      enabled_currencies: [],
      show_full_pricing_data: true,
    };
    this.totalSKUs = 0;
    this.totalStock = 0;
    this.assignedAgents = this.newCatalog.agents.length;
    this.assignedLocations = [];
    this.assignedAudiences = 0;
    this.assignedCustomers = [];
    this.saveAs = 'Draft';
    this.catalogService.setNewCatalogData(this.newCatalog);
  }

  /**
   * Calculate unique SKU count from products and their variants
   * Excludes empty, null, undefined, or "0" SKUs
   * If SKU count is 0 but products exist, fallback to product count
   */
  private calculateUniqueSKUs(products: IProduct[]): number {
    if (!products || products.length === 0) {
      return 0;
    }
    
    const uniqueSKUs = new Set<string>();
    
    products.forEach(product => {
      // Check product SKU (for products without variants or as fallback)
      if (product.sku && product.sku !== '0' && product.sku.trim() !== '') {
        uniqueSKUs.add(product.sku);
      }
      
      // Check variant SKUs (for products with variants)
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach(variant => {
          if (variant.sku && variant.sku !== '0' && variant.sku.trim() !== '') {
            uniqueSKUs.add(variant.sku);
          }
        });
      }
    });
    
    // If no valid SKUs found but products exist, use product count as fallback
    if (uniqueSKUs.size === 0 && products.length > 0) {
      // Count unique products (by id) as fallback
      const uniqueProductIds = new Set(
        products.map(p => p.id ?? p.product_id ?? 0).filter(id => id > 0)
      );
      return uniqueProductIds.size > 0 ? uniqueProductIds.size : products.length;
    }
    
    return uniqueSKUs.size;
  }

  selectedCompaniesChange(companies: number[]) {
    this.selectedCompanies = companies;
  }

  selectedCompaniesChanged(hasSelection: boolean) {
    this.showDeleteButton = hasSelection;
  }

  assignedLocationsChange(locations: number[]) {
    this.assignedLocations = locations;
    this.assignedAudiences = locations.length;
    this.catalogService.setNewCatalogData({ ...this.newCatalog, locations_ids: locations });
  }

  openAddPortfolioDialog() {
    if(!this.newCatalog) return;
    this.selectedCompanies = [];
    this.showDeleteButton = false;
    this.clearSelectedCompanies = 'clear';

    this.dialog.open(AgentPortfolioAddDialogComponent, {
      data: {
        agent: '',
        locations_ids: this.newCatalog?.locations_ids || [],
        subsidiary_id: this.newCatalog?.subsidiary_id || undefined,
        type: 'client'
      },
    }).afterClosed().subscribe((result: any) => {
      if(result) {
        this.assignedLocationsChange(result.locations_ids);

        // Merge new customers with existing ones to prevent data loss of pre-existing assignments
        // that weren't loaded in the dialog (e.g. if dialog only loaded first page)
        const newCustomerIds = new Set(result.customers.map((c: ICustomer) => c.id));
        const validLocationIds = new Set(result.locations_ids);

        // 1. Keep existing customers if they still have at least one assigned location
        const keptExistingCustomers = this.assignedCustomers.filter(c => {
          // If the customer was returned by the dialog, we'll use the new version (it might have updated locations)
          if(newCustomerIds.has(c.id)) return false;

          // Otherwise, check if it still has assigned locations
          return c.locations?.some(l => validLocationIds.has(l.id!));
        });

        // 2. Add new customers from dialog
        this.assignedCustomers = [...keptExistingCustomers, ...result.customers];

        this.catalogService.setNewCatalogData({ ...this.newCatalog, customers: this.assignedCustomers });
        if(this.isEdit) {
          this.save(this.newCatalog.status === 'Draft' ? 'Draft' : 'Approved')
        }
      }
    });
  }

  deleteAudience() {
    if(!this.validToSubmit) return;
    const customersToRemove = this.assignedCustomers.filter((customer: ICustomer) => this.selectedCompanies.includes
      (customer.id!));
    const newAssignedCustomers = this.assignedCustomers.filter((customer: ICustomer) => !this.selectedCompanies.includes(customer.id!));

    if(!this.isEdit) {
      this.newCatalog.locations_ids = this.newCatalog.locations_ids?.filter((id: number) => !customersToRemove.some((customer: ICustomer) => customer.locations?.some((location: ILocation) => location.id === id)));

      this.catalogService.setNewCatalogData({ ...this.newCatalog, locations_ids: this.newCatalog.locations_ids, customers: newAssignedCustomers });
      this.assignedCustomers = newAssignedCustomers;
    } else if(this.isEdit) {
      const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
        width: '600px',
      });
      dialogRef.afterClosed().subscribe((result: boolean) => {
        if(result) {
          this.newCatalog.locations_ids = this.newCatalog.locations_ids?.filter((id: number) => !customersToRemove.some((customer: ICustomer) => customer.locations?.some((location: ILocation) => location.id === id)));

          this.catalogService.setNewCatalogData({ ...this.newCatalog, locations_ids: this.newCatalog.locations_ids, customers: newAssignedCustomers });
          this.assignedCustomers = newAssignedCustomers;
          this.save(this.newCatalog.status === 'Draft' ? 'Draft' : 'Approved')
        }
      });
    }
    this.showDeleteButton = false;
    this.selectedCompanies = [];
    this.clearSelectedCompanies = 'delete';
  }

  // Sorting state
  currentSortBy: string = 'name';
  currentSortMode: 'asc' | 'desc' = 'asc';

  /**
   * Handle sort change from audience list
   */
  onSortChange(event: { sortBy: string; sortMode: 'asc' | 'desc' }): void {
    this.currentSortBy = event.sortBy;
    this.currentSortMode = event.sortMode;

    // Reload first page with new sort
    this.loadInitialCompaniesPage();
  }

  /**
   * Load initial page of companies for catalog audience (edit mode)
   * This is called when opening an existing catalog to fetch the first page of assigned companies
   */
  loadInitialCompaniesPage(): void {
    if(!this.catalogtId) {
      return;
    }

    this.isLoadingMoreCompanies = true;

    // Dispatch action to fetch first page of companies assigned to this catalog
    this.store.dispatch(GetCatalogAudience({
      page: 1,
      limit: 20,
      sortBy: this.currentSortBy,
      sortMode: this.currentSortMode,
      pricelistId: this.catalogtId
    }));
  }

  /**
   * Handle audience companies loaded from NgRx actions
   * @param companies The companies loaded from the API
   * @param metadata Pagination metadata
   * @param isAppend Whether to append to existing list or replace
   */
  private handleAudienceCompaniesLoaded(companies: ICustomer[], metadata: any, isAppend: boolean): void {
    if (isAppend) {
      // Append new companies to existing list
      this.assignedCustomers = [...this.assignedCustomers, ...companies];
    } else {
      // Replace with new list
      this.assignedCustomers = companies;
    }

    this.companiesMetadata = metadata;

    // Preserve assignedLocations - only set if not already initialized (for initial load)
    if (!isAppend && (!this.assignedLocations || this.assignedLocations.length === 0)) {
      this.assignedLocations = this.assignedCustomers.flatMap(
        (company: ICustomer) => company.locations?.map((location: ILocation) => location.id!) ?? []
      );
    }
    this.assignedAudiences = this.assignedLocations.length;

    if (this.newCatalog) {
      this.newCatalog.customers = this.assignedCustomers;
      this.newCatalog.companies = this.assignedCustomers;
      // Preserve full locations_ids list across pagination
      if (!isAppend && (!this.newCatalog.locations_ids || this.newCatalog.locations_ids.length === 0)) {
        this.newCatalog.locations_ids = this.assignedLocations;
      }
      this.catalogService.setNewCatalogData(this.newCatalog);
    }

    this.isLoadingMoreCompanies = false;
  }

  /**
   * Handle load more for audience companies list
   * This method would be called when user scrolls to the bottom
   */
  loadMoreCompanies(): void {
    // Check if already loading or no more data
    if (this.isLoadingMoreCompanies || !this.companiesMetadata) {
      return;
    }

    const nextPage = this.companiesMetadata.page + 1;

    // Check if there are more pages
    if (nextPage > this.companiesMetadata.pages) {
      return;
    }

    this.isLoadingMoreCompanies = true;

    // Dispatch action to load next page
    this.store.dispatch(LoadMoreCatalogAudience({
      page: nextPage,
      limit: 20,
      sortBy: this.currentSortBy,
      sortMode: this.currentSortMode,
      pricelistId: this.catalogtId
    }));
  }

  deleteLocations(locationId: number) {
    if(!this.validToSubmit) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if(result) {
        this.newCatalog.locations_ids = this.newCatalog.locations_ids?.filter((id: number) => id !== locationId);
        this.catalogService.setNewCatalogData({ ...this.newCatalog, locations_ids: this.newCatalog.locations_ids });
        this.save(this.newCatalog.status === 'Draft' ? 'Draft' : 'Approved')
      }
    });
  }

  isAllFormsValid(isValid: boolean) {
    this.validToSubmit = isValid;
  }

  private determineProductHandlingUnits(
    product: IProduct,
    catalogData: INewCatalog,
    catalogDefaultChanged: boolean = false
  ): { enabledHandlingUnitIds: number[]; defaultHandlingUnitId: number | null } {
    const productHandlingUnits = product.handling_units || [];
    const catalogEnabledHUIds = catalogData.enabled_handling_unit_ids || [];
    const productDefaultHUId = this.getProductDefaultHUId(product);
    
    // Get product supported HU type IDs
    const productSupportedHUIds = productHandlingUnits
      .map((hu: any) => hu.type_id || hu.id)
      .filter((id: any): id is number => !!id);
    
    // Enabled = intersection of catalog enabled and product supported
    const enabledHandlingUnitIds = catalogEnabledHUIds.filter(id => productSupportedHUIds.includes(id));
    
    // Determine default: preserve if still valid and catalog default unchanged
    let defaultHandlingUnitId: number | null = null;
    if (productDefaultHUId && enabledHandlingUnitIds.includes(productDefaultHUId) && !catalogDefaultChanged) {
      defaultHandlingUnitId = productDefaultHUId;
    } else if (catalogData.default_handling_unit_id && enabledHandlingUnitIds.includes(catalogData.default_handling_unit_id)) {
      defaultHandlingUnitId = catalogData.default_handling_unit_id;
    } else if (enabledHandlingUnitIds.length > 0) {
      defaultHandlingUnitId = enabledHandlingUnitIds[0];
    }
    
    return { enabledHandlingUnitIds, defaultHandlingUnitId };
  }

  private checkCatalogHUChanged(data: INewCatalog): { enabledChanged: boolean; defaultChanged: boolean } {
    const currentCatalogEnabledHUIds = data.enabled_handling_unit_ids || [];
    const currentCatalogDefaultHUId = data.default_handling_unit_id || null;
    
    const enabledHUIdsChanged = JSON.stringify([...this.previousCatalogEnabledHUIds].sort()) !== JSON.stringify([...currentCatalogEnabledHUIds].sort());
    const defaultHUIdChanged = this.previousCatalogDefaultHUId !== currentCatalogDefaultHUId;
    
    return { 
      enabledChanged: enabledHUIdsChanged, 
      defaultChanged: defaultHUIdChanged 
    };
  }

  private getProductDefaultHUId(product: IProduct): number | null {
    if ((product as any)?.default_handling_unit_id) {
      return (product as any).default_handling_unit_id;
    }
    const defaultHU = product.handling_units?.find((hu: any) => hu.default === true);
    return defaultHU ? (defaultHU.type_id || defaultHU.id) : null;
  }

  private updateProductHandlingUnits(product: IProduct, catalogData: INewCatalog, catalogHUChanged: { enabledChanged: boolean; defaultChanged: boolean }): any[] {
    let enabledHandlingUnitIds: number[];
    let defaultHandlingUnitId: number | null;
    
    if (catalogHUChanged.enabledChanged || catalogHUChanged.defaultChanged) {
      const result = this.determineProductHandlingUnits(product, catalogData, catalogHUChanged.defaultChanged);
      enabledHandlingUnitIds = result.enabledHandlingUnitIds;
      defaultHandlingUnitId = result.defaultHandlingUnitId;
    } else {
      enabledHandlingUnitIds = (product as any)?.enabled_handling_unit_ids || [];
      defaultHandlingUnitId = this.getProductDefaultHUId(product);
    }
    
    return (product.handling_units || []).map((hu: any) => ({
      ...hu,
      enabled: enabledHandlingUnitIds.includes(hu.type_id || hu.id),
      default: defaultHandlingUnitId === (hu.type_id || hu.id)
    }));
  }

  private updatePreviousCatalogHUValues(data: INewCatalog): void {
    this.previousCatalogEnabledHUIds = [...(data.enabled_handling_unit_ids || [])];
    this.previousCatalogDefaultHUId = data.default_handling_unit_id || null;
  }

  save(as: 'Draft' | 'Approved') {
    console.log('save');
    
    if(!this.validToSubmit) return;
    this.saveAs = as;
    const data = this.newCatalog;

    if(!data) return;

    const catalogHUChanged = this.checkCatalogHUChanged(data);
    let products: any = [];
    
    if(this.source === 'agents') {
      if(data.products) {
        const isMarginMode = data.pricing_rule === 'margin';
        const overallPricingValue = data.overall_pricing_value || 0;
        
        products = data.products?.map((product: IProduct) => {
          const { id, final_price, discount, extra_discount, currencies, ...rest } = product;
          const hasCustomDiscount = (product as any).hasCustomDiscount || false;
          const productDiscount = Number(discount) || 0;
          const isCustomDiscount = hasCustomDiscount || (productDiscount !== 0 && productDiscount !== overallPricingValue);
          const discountValue = isCustomDiscount ? productDiscount : (Number(overallPricingValue) || 0);
          let calculatedFinalPrice = 0;
          
          if (isCustomDiscount && final_price && final_price > 0) {
            calculatedFinalPrice = Number(final_price);
          } else {
            const defaultCurrency = product.currencies?.find(c => c.currency_id === data.default_currency);
            const finalPriceFromCurrency = defaultCurrency?.final_price;
            
            if (isMarginMode) {
              const cost = product.cost || defaultCurrency?.cost || 0;
              if (cost && cost > 0) {
                calculatedFinalPrice = Number((cost * (1 + discountValue / 100)).toFixed(2));
              } else if (finalPriceFromCurrency && finalPriceFromCurrency > 0) {
                calculatedFinalPrice = Number(finalPriceFromCurrency);
              } else if (final_price && final_price > 0) {
                calculatedFinalPrice = Number(final_price);
              }
            } else {
              const salesPrice = Number(product.sales_price) || Number(defaultCurrency?.sales_price) || 0;
              if (salesPrice > 0) {
                calculatedFinalPrice = Number((salesPrice * (1 - discountValue / 100)).toFixed(2));
              } else if (finalPriceFromCurrency && finalPriceFromCurrency > 0) {
                calculatedFinalPrice = Number(finalPriceFromCurrency);
              } else if (final_price && final_price > 0) {
                calculatedFinalPrice = Number(final_price);
              }
            }
          }
          
          const updatedHandlingUnits = this.updateProductHandlingUnits(product, data, catalogHUChanged);
          
          return {
            ...rest,
            id: Number(product.id),
            product_template_id: product.product_template_id,
            final_price: calculatedFinalPrice,
            discount: discountValue,
            max_extra_discount: extra_discount || null,
            increment: product.increment || data.quantity_rules?.increment || 1,
            max_qte: product.max_qte || data.quantity_rules?.max_qte || null,
            min_qte: product.min_qte || data.quantity_rules?.min_qte || 1,
            handling_units: updatedHandlingUnits,
            can_be_sold_out_of_stock: product.can_be_sold_out_of_stock
          }
        })
      }
      
      this.updatePreviousCatalogHUValues(data);

      const payload: Partial<INewCatalogDTO> = {
          subsidiary_id: data.subsidiary_id || undefined,
          status: as,
          name: data.name,
          description: data.description || null,
          image: data.coverUrl || null,
          end_date: data.expiration_date ? new Date(data.expiration_date).toISOString().split('T')[0] : null,
          overall_pricing_rule: data.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp',
          overall_pricing_value: data.overall_pricing_value || 0,
          max_extra_discount: data.extra_discount || null,
          block_order_on_max_extra_discount: data.extra_discount_blocking || false,
          min_order_amount: data.min_order_amount || null,
          increment: data.quantity_rules?.increment || 1,
          max_qte: data.quantity_rules?.max_qte || null,
          min_qte: data.quantity_rules?.min_qte || 1,
          can_be_sold_out_of_stock: data.quantity_rules?.can_be_sold_out_of_stock || false,
          show_stock: data.quantity_rules?.show_stock === true ? true : false,
          agent_ids: data.agents ? data.agents?.map((agent) => agent.id!) : [],
          enabled_handling_unit_ids: data.enabled_handling_unit_ids || [],
          default_handling_unit_id: data.default_handling_unit_id || null,
          show_full_pricing_data: data.show_full_pricing_data ?? true,
      };

      if(!this.isEdit) {
        payload['products'] = products;
        this.store.dispatch(AddAgentCatalog({
          catalog: payload
        }))
      } else {
        payload['products'] = products;
        this.store.dispatch(UpdateAgentCatalog({ id: this.catalogtId, catalog: payload }))
      }
    } else {
      if(data.products){
        const isMarginMode = data.pricing_rule === 'margin';
        const isSingleCurrency = !data.is_multi_currency;
        const supportedCurrencyIds = isSingleCurrency 
          ? (data.default_currency ? [data.default_currency] : [])
          : (data.enabled_currencies && data.enabled_currencies.length > 0 
              ? data.enabled_currencies 
              : (data.default_currency ? [data.default_currency] : []));
        
        products = data.products?.map((product: IProduct) => {
          let currencies = product.currencies?.filter((currency: ICurrencyPrice) => 
            supportedCurrencyIds.includes(currency.currency_id!)
          );
          const productDiscount = product.discount ?? data.overall_pricing_value ?? 0;
          
          if (currencies) {
            currencies = currencies.map((currency: ICurrencyPrice | IProductCurrency) => {
              const currencyWithCost = currency as any;
              
              if (isMarginMode) {
                const cost = currencyWithCost.cost ?? product.cost ?? 0;
                if (cost && cost > 0) {
                  const finalPrice = Number((cost * (1 + productDiscount / 100)).toFixed(2));
                  return {
                    ...currency,
                    final_price: finalPrice
                  } as any;
                } else if (currencyWithCost.final_price && currencyWithCost.final_price > 0) {
                  return currency;
                }
              } else {
                const salesPrice = currencyWithCost.sales_price ?? product.sales_price ?? 0;
                if (salesPrice > 0) {
                  const finalPrice = Number((salesPrice * (1 - productDiscount / 100)).toFixed(2));
                  return {
                    ...currency,
                    final_price: finalPrice
                  } as any;
                } else if (currencyWithCost.final_price && currencyWithCost.final_price > 0) {
                  return currency;
                }
              }
              
              return currency;
            });
          }
          
          const { final_price, ...rest } = product;
          const updatedHandlingUnits = this.updateProductHandlingUnits(product, data, catalogHUChanged);
          
          return {
            ...rest,
            id: Number(product.id),
            currencies: currencies,
            handling_units: updatedHandlingUnits,
          };
        })
      }
      
      this.updatePreviousCatalogHUValues(data);
      // Compute unloaded_product_ids: products that were never loaded on frontend
      // These are products beyond the pagination that should be preserved during update
      const allProductIds = new Set(this.newCatalog.product_ids || []);
      const loadedProductIds = new Set(this.newCatalog.loaded_product_ids || []);
      const unloadedProductIds = [...allProductIds].filter(id => !loadedProductIds.has(id));

      const payload: Omit<ICatalog, 'id'> = {
        subsidiary_id: data.subsidiary_id || undefined,
        name: data.name,
        description: data.description,
        image: data.coverUrl ?? '',
        products,
        locations_ids: this.newCatalog.locations_ids!,
        unloaded_product_ids: unloadedProductIds,
        end_date: data.expiration_date ? new Date(data.expiration_date).toISOString().split('T')[0] : null,
        status: this.saveAs === 'Approved' ? 'Approved' : this.newCatalog.status,
        overall_pricing_rule: data.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp',
        overall_pricing_value: data.overall_pricing_value || 0,
        increment: data.quantity_rules?.increment || 1,
        max_qte: data.quantity_rules?.max_qte || null,
        min_qte: data.quantity_rules?.min_qte || 1,
        min_order_amount: data.min_order_amount || null,
        can_be_sold_out_of_stock: data.quantity_rules?.can_be_sold_out_of_stock || false,
        show_stock: data.quantity_rules?.show_stock === true ? true : false,
        is_single_currency: !data.is_multi_currency,
        default_currency_id: data.default_currency,
        currency_ids: !data.is_multi_currency 
          ? (data.default_currency ? [data.default_currency] : [])
          : (data.enabled_currencies && data.enabled_currencies.length > 0 
              ? data.enabled_currencies 
              : (data.default_currency ? [data.default_currency] : [])),
        enabled_handling_unit_ids: data.enabled_handling_unit_ids || [],
        default_handling_unit_id: data.default_handling_unit_id || null,
        show_full_pricing_data: data.show_full_pricing_data ?? true,
      }

      if(!this.isEdit) {
        payload['custom_fields'] = data.custom_fields || {};
        this.store.dispatch(AddCatalog({ catalog: payload }))
      } else {
        this.store.dispatch(UpdateCatalog({ id: this.catalogtId, catalog: payload }))
      }
    }
  }

  openShareCatalog(option?: 'add' | 'edit'): void {
    const data = this.newCatalog;
    let products: any = [];
    if(option === 'edit' && this.source === 'agents') {
      if(data.products) {
        products = data.products?.map((product: IProduct) => {
          const { id, final_price, discount, extra_discount, max_extra_discount } = product
          // Use discount field for both discount and margin (for margin on cost catalog)
          const discountValue = Number(discount) || 0;

          return {
            id: Number(product.id),
            final_price: Number(final_price) || 0,
            discount: discountValue,
            max_extra_discount: extra_discount || max_extra_discount || null,
            increment: data.quantity_rules?.increment || 1,
            max_qte: data.quantity_rules?.max_qte || null,
            min_qte: data.quantity_rules?.min_qte || 1,
          }
        })
      }

      const payload: INewCatalogDTO = {
          status: 'Approved',
          name: data.name,
          description: data.description || null,
          image: data.coverUrl || null,
          end_date: data.expiration_date ? new Date(data.expiration_date).toISOString().split('T')[0] : null,
          overall_pricing_rule: data.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp',
          overall_pricing_value: data.overall_pricing_value || 0,
          max_extra_discount: data.extra_discount || null,
          block_order_on_max_extra_discount: data.extra_discount_blocking || false,
          min_order_amount: data.min_order_amount || null,
          increment: data.quantity_rules?.increment || 1,
          max_qte: data.quantity_rules?.max_qte || null,
          min_qte: data.quantity_rules?.min_qte || 1,
          can_be_sold_out_of_stock: data.quantity_rules?.can_be_sold_out_of_stock || false,
          show_stock: data.quantity_rules?.show_stock === true ? true : false,
          products,
          agent_ids: data.agents ? data.agents?.map((agent) => agent.id!) : [],
          enabled_handling_unit_ids: data.enabled_handling_unit_ids || [],
          default_handling_unit_id: data.default_handling_unit_id || null,
      };

      this.store.dispatch(UpdateAgentCatalog({ id: this.catalogtId, catalog: payload }))
      // this.router.navigate(['catalogs/agents-catalog/edit-catalog', this.catalogtId]);
      return;
    }
    const dialogRef = this.dialog.open(ShareCatalogComponent, {
      width: '600px',
      disableClose: false,
      data: {
        catalogId: this.catalogtId,
        catalogName: this.newCatalog.name
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        const confirmNotificationRef = this.dialog.open(ConfirmNotificationComponent, {
          width: '600px',
          data: {
            notifiedCount: result.notifiedCount
          }
        });
        confirmNotificationRef.afterClosed().subscribe(() => {
          this.router.navigate(['catalogs/edit-catalog', this.catalogtId]);
          this.fetchActions();
        });
      } else if(option === 'add') {
        this.router.navigate(['catalogs/edit-catalog', this.catalogtId]);
      }
    });
  }

  downloadCatalog() {
    this.store.dispatch(DownloadCatalog({ id: this.catalogtId }));
  }


  getValidationTooltipMessage(): string {
    const pricingRule = this.newCatalog.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return getValidationTooltipMessage(
      this.newCatalog.products,
      this.availableCurrencies,
      this.translateService,
      this.source,
      pricingRule,
      this.newCatalog.default_currency
    );
  }

  areSelectedProductsValid(): boolean {
    const pricingRule = this.newCatalog.pricing_rule === 'margin' ? 'margin_on_cost' : 'discount_on_msrp';
    return areSelectedProductsValid(
      this.newCatalog.products,
      this.availableCurrencies,
      this.translateService,
      this.source,
      pricingRule,
      this.newCatalog.default_currency
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(ClearCatalogAudience());
  }
}
